/**
 * The call center hours between the country document and its form.
 *
 * ISO 8601 keys: Monday=1 … Sunday=7, all seven always written. The label is
 * hand-written and not taken from moment: `moment.locale` is process global
 * state, and changing it here would change date formatting across the CRM.
 */
export const WEEKDAYS = [
  [1, 'Monday'],
  [2, 'Tuesday'],
  [3, 'Wednesday'],
  [4, 'Thursday'],
  [5, 'Friday'],
  [6, 'Saturday'],
  [7, 'Sunday'],
];

// The two closure lists are arrays on Firestore and text in the form, as
// `negativeReasons` on the acquisition order and `reasons` on the
// monetization order already are.
const linesFromArray = (value) =>
  Array.isArray(value) ? value.join('\n') : '';

const arrayFromLines = (text) =>
  String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/**
 * What the form starts from. There are no default hours: every country has
 * its own call center, and a prefilled week would be saved as the truth by
 * whoever does not look. A country without hours starts with every day
 * closed and empty.
 *
 * All seven days are always there, even empty: Formik builds a missing
 * parent from the next key of the path, and a numeric key would turn
 * `workingTime` into an array.
 *
 * A closed day keeps its `start` and `end`: the form flips the switch and
 * keeps the values, so opening on Sunday for a month of campaign does not
 * mean typing them again from memory.
 */
export const callCenterFormValues = (callCenter) => {
  const saved = callCenter || {};
  const workingTime = {};
  WEEKDAYS.forEach(([day]) => {
    const savedDay = (saved.workingTime && saved.workingTime[day]) || {};
    workingTime[day] = {
      open: savedDay.open === true,
      start: savedDay.start || '',
      end: savedDay.end || '',
    };
  });

  return {
    workingTime,
    recurringClosedDays: linesFromArray(saved.recurringClosedDays),
    closedDays: linesFromArray(saved.closedDays),
  };
};

/**
 * What the country document gets, or null when no weekday is open.
 *
 * null and not a week of closed days: the cloud functions treat any
 * `callCenter.workingTime` as a call center configured, so "closed every day"
 * would read as "always closed", and with the AI routing set to work only
 * while the call center is closed the agent would take every lead. A country
 * with no open day has no call center, and the field has to go.
 *
 * `open` is what holds a day shut, not a missing key — a save with merge
 * would never delete a removed one.
 */
export const callCenterFromForm = (formCallCenter) => {
  const form = formCallCenter || {};
  const workingTime = {};
  WEEKDAYS.forEach(([day]) => {
    const formDay = (form.workingTime && form.workingTime[day]) || {};
    workingTime[day] = {
      open: formDay.open === true,
      start: formDay.start || '',
      end: formDay.end || '',
    };
  });

  if (!Object.values(workingTime).some(({ open }) => open)) return null;

  return {
    workingTime,
    recurringClosedDays: arrayFromLines(form.recurringClosedDays),
    closedDays: arrayFromLines(form.closedDays),
  };
};
